import React, { useState } from "react";
import {
    Outlet,
    NavLink,
    Link,
    useNavigate,
    useLocation,
} from "react-router-dom";
import { useAuth } from "../auth/AuthProvider";
import DevControls from "../features/common/components/DevControls";
import ChatButton from "../features/chat/components/ChatButton";
import { RouteErrorBoundary } from "../components/ErrorBoundary";
import GlobalSearch from "../components/GlobalSearch";

const UserLayout: React.FC = () => {
    const [sidebarOpen, setSidebarOpen] = useState(true);
    const [docsOpen, setDocsOpen] = useState(false);
    const [confirmOpen, setConfirmOpen] = useState(false);

    const nav = useNavigate();
    const { logout, isPartner } = useAuth();
    const { hash, pathname } = useLocation();

    const logoBox = "h-8 w-8 shrink-0";
    const iconBox = "h-5 w-5 shrink-0";
    const baseRow =
        "h-10 w-full rounded-lg transition-colors flex items-center gap-3 px-3";
    const hoverRow = "hover:bg-surface-600";
    const activeRow = "bg-quantum-500/15 text-quantum-400";
    const inactiveText = "text-text-secondary";
    const labelCls = sidebarOpen ? "" : "hidden";

    const handleLogoClick = () => {
        if (!sidebarOpen) setSidebarOpen(true);
        else nav("/app/dashboard");
    };

    return (
        <div className="min-h-screen bg-surface-900 text-text-primary">
            {/* Confirm logout modal */}
            {confirmOpen && (
                <div
                    className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 backdrop-blur-sm"
                    onClick={() => setConfirmOpen(false)}
                >
                    <div
                        className="w-full max-w-sm rounded-xl bg-surface-700 border border-border p-6 shadow-xl"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <h3 className="mb-4 text-h4 text-text-primary">
                            Confirm Logout
                        </h3>
                        <p className="text-body-sm text-text-secondary">
                            Are you sure you want to log out?
                        </p>
                        <div className="mt-6 flex justify-end gap-3">
                            <button
                                className="btn-secondary"
                                onClick={() => setConfirmOpen(false)}
                            >
                                Cancel
                            </button>
                            <button
                                className="btn-primary"
                                onClick={() => {
                                    logout();
                                    nav("/login", { replace: true });
                                }}
                            >
                                Log out
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Sidebar */}
            <aside
                className={`fixed top-0 left-0 z-10 h-screen bg-surface-800 border-r border-border transition-all duration-300 ${
                    sidebarOpen ? "w-64" : "w-16"
                }`}
            >
                <nav className="flex h-full flex-col p-4 text-body-sm">
                    {/* Logo */}
                    <div
                        onClick={handleLogoClick}
                        className={`${baseRow} relative cursor-pointer group mb-2`}
                    >
                        {!sidebarOpen && (
                            <span
                                className="absolute -right-4 top-1 h-8 w-1 rounded-l bg-quantum-500 opacity-0 transition-opacity duration-150 group-hover:opacity-100"
                                aria-hidden
                            />
                        )}

                        <div className={`relative ${logoBox}`}>
                            <img
                                src="/quantum-small.png"
                                alt="Quantum"
                                className={`${logoBox} transition-opacity group-hover:opacity-80`}
                            />
                        </div>

                        <span
                            className={`${labelCls} ml-1 select-none text-lg font-semibold text-text-primary`}
                        >
                            TotalView
                        </span>

                        {sidebarOpen && (
                            <SidebarToggleIcon
                                className={`${iconBox} ml-auto cursor-pointer text-text-muted hover:text-text-primary transition-colors`}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setSidebarOpen(false);
                                }}
                            />
                        )}
                    </div>

                    {/* Search shortcut hint */}
                    <button
                        onClick={() => window.dispatchEvent(new KeyboardEvent("keydown", { key: "k", metaKey: true }))}
                        className={`${baseRow} ${hoverRow} ${inactiveText} group`}
                    >
                        <SearchHintIcon className={iconBox} />
                        <span className={labelCls}>Search</span>
                        {sidebarOpen && (
                            <kbd className="ml-auto px-1.5 py-0.5 rounded bg-surface-700 text-[10px] text-text-muted border border-border font-mono">
                                {navigator.platform?.includes("Mac") ? "\u2318K" : "Ctrl+K"}
                            </kbd>
                        )}
                    </button>

                    {/* Main menu */}
                    <div className="mt-4 space-y-1 flex-1 overflow-y-auto">
                        <NavLink
                            to="/app/dashboard"
                            className={({ isActive }) =>
                                `${baseRow} ${isActive ? activeRow : `${hoverRow} ${inactiveText}`}`
                            }
                        >
                            <PortfolioIcon className={iconBox} />
                            <span className={labelCls}>Portfolio</span>
                        </NavLink>

                        <NavLink
                            to="/app/projects"
                            className={({ isActive }) =>
                                `${baseRow} ${isActive ? activeRow : `${hoverRow} ${inactiveText}`}`
                            }
                        >
                            <ProjectsIcon className={iconBox} />
                            <span className={labelCls}>Projects</span>
                        </NavLink>

                        {isPartner && (
                            <NavLink
                                to="/app/clients"
                                className={({ isActive }) =>
                                    `${baseRow} ${isActive ? activeRow : `${hoverRow} ${inactiveText}`}`
                                }
                            >
                                <ClientsIcon className={iconBox} />
                                <span className={labelCls}>Clients</span>
                            </NavLink>
                        )}

                        <NavLink
                            to="/app/loads"
                            className={({ isActive }) =>
                                `${baseRow} ${isActive ? activeRow : `${hoverRow} ${inactiveText}`}`
                            }
                        >
                            <LoadPortfolioIcon className={iconBox} />
                            <span className={labelCls}>Loads</span>
                        </NavLink>

                        <NavLink
                            to="/app/api-keys"
                            className={({ isActive }) =>
                                `${baseRow} ${isActive ? activeRow : `${hoverRow} ${inactiveText}`}`
                            }
                        >
                            <KeyIcon className={iconBox} />
                            <span className={labelCls}>API Keys</span>
                        </NavLink>

                        <NavLink
                            to="/app/settings"
                            className={({ isActive }) =>
                                `${baseRow} ${isActive ? activeRow : `${hoverRow} ${inactiveText}`}`
                            }
                        >
                            <SettingsIcon className={iconBox} />
                            <span className={labelCls}>Settings</span>
                        </NavLink>

                        {/* Support / Docs */}
                        <div className="space-y-1">
                            <div
                                onClick={() => {
                                    setDocsOpen(false);
                                    nav("/app/support");
                                }}
                                className={`${baseRow} cursor-pointer ${
                                    pathname === "/app/support" && hash === ""
                                        ? activeRow
                                        : `${hoverRow} ${inactiveText}`
                                }`}
                            >
                                <DocsIcon className={iconBox} />
                                <span className={labelCls}>Support / Docs</span>

                                {sidebarOpen && (
                                    <ChevronIcon
                                        className={`${iconBox} ml-auto transition-transform ${
                                            docsOpen ? "rotate-180" : ""
                                        }`}
                                        onClick={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            setDocsOpen(!docsOpen);
                                        }}
                                    />
                                )}
                            </div>

                            {docsOpen && sidebarOpen && (
                                <div className="ml-8 space-y-1">
                                    <Link
                                        to="/app/support#user-docs"
                                        className={`h-8 px-3 rounded-lg flex items-center text-body-sm ${
                                            pathname === "/app/support" && hash === "#user-docs"
                                                ? activeRow
                                                : `${hoverRow} ${inactiveText}`
                                        }`}
                                    >
                                        User Docs
                                    </Link>
                                    <Link
                                        to="/app/support#developer-docs"
                                        className={`h-8 px-3 rounded-lg flex items-center text-body-sm ${
                                            pathname === "/app/support" && hash === "#developer-docs"
                                                ? activeRow
                                                : `${hoverRow} ${inactiveText}`
                                        }`}
                                    >
                                        Developer Docs
                                    </Link>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Logout */}
                    <button
                        onClick={() => setConfirmOpen(true)}
                        className={`${baseRow} mt-4 ${hoverRow} ${inactiveText}`}
                    >
                        <LogoutIcon className={iconBox} />
                        <span className={labelCls}>Logout</span>
                    </button>
                </nav>
            </aside>

            {/* Main content */}
            <main
                className={`min-h-screen transition-all duration-300 ${
                    sidebarOpen ? "ml-64" : "ml-16"
                }`}
            >
                {process.env.NODE_ENV === 'development' && <DevControls />}
                <RouteErrorBoundary>
                    <Outlet />
                </RouteErrorBoundary>
                <ChatButton />
            </main>
            <GlobalSearch />
        </div>
    );
};

export default UserLayout;

// Icons
function HomeIcon({ className }: { className?: string }) {
    return (
        <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
        </svg>
    );
}

function ProjectsIcon({ className }: { className?: string }) {
    return (
        <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
        </svg>
    );
}

function ClientsIcon({ className }: { className?: string }) {
    return (
        <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
        </svg>
    );
}

function PortfolioIcon({ className }: { className?: string }) {
    return (
        <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
        </svg>
    );
}

function LoadPortfolioIcon({ className }: { className?: string }) {
    return (
        <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 12h14M5 12a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v4a2 2 0 01-2 2M5 12a2 2 0 00-2 2v4a2 2 0 002 2h14a2 2 0 002-2v-4a2 2 0 00-2-2m-2-4h.01M17 16h.01" />
        </svg>
    );
}

function KeyIcon({ className }: { className?: string }) {
    return (
        <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
        </svg>
    );
}

function SettingsIcon({ className }: { className?: string }) {
    return (
        <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
    );
}

function DocsIcon({ className }: { className?: string }) {
    return (
        <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
        </svg>
    );
}

function LogoutIcon({ className }: { className?: string }) {
    return (
        <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
        </svg>
    );
}

function ChevronIcon({ className, onClick }: { className?: string; onClick?: (e: React.MouseEvent) => void }) {
    return (
        <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" onClick={onClick}>
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
    );
}

function SidebarToggleIcon({ className, onClick }: { className?: string; onClick?: (e: React.MouseEvent) => void }) {
    return (
        <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" onClick={onClick}>
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h7" />
        </svg>
    );
}

function SearchHintIcon({ className }: { className?: string }) {
    return (
        <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
        </svg>
    );
}
